import { useMemo, useState } from 'react'
import { DEFAULT_STAGE_PROBABILITIES } from '@repo/types'

import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal/ConfirmSettingModal'
import { CreateDemoModal } from 'components/feedback/modals/CreateDemoModal'
import { useCanAccess } from 'hooks'
import { useSnackbar } from 'contexts'
import {
  useCloseSalesDeal,
  useCreateSalesAccount,
  useCreateSalesDeal,
  useCreateSalesLead,
  useDeleteSalesLead,
  useMoveSalesDeal,
  useReopenSalesDeal,
  useSalesAccount,
  useSalesAccounts,
  useSalesBoard,
  useSalesLeads,
  useUpdateSalesAccount,
  useUpdateSalesDeal,
  useUpdateSalesLead,
  useUpdateSalesSettings,
  useUpdateSalesTagEconomics,
} from 'hooks/api/useGtmSalesPipeline'
import type { SalesDealCard, SalesLeadRow } from 'services'
import { getErrorMessage } from 'utils'

import type { PendingMove } from './DealsBoard'
import { dealTitle, STAGE_LABEL } from './dealsModel'
import {
  CloseDealModal,
  ConfirmStageMoveModal,
  DealFormModal,
  LeadFormModal,
  SalesCompanyFormModal,
  StageProbabilitiesModal,
  TagEconomicsModal,
} from './salesForms'
import { SalesCompanyDrawer } from './SalesCompanyDrawer'

export interface SalesWorkspaceActions {
  addLead: (accountId?: string | null) => void
  addCompany: () => void
  /** VRG-701 — Add deal, optionally at a company already chosen. */
  addDeal: (accountId?: string | null) => void
  /** Open the company drawer, on one of its deals when given (VRG-701: a company has several). */
  openCompany: (accountId: string, dealId?: string | null) => void
  editLead: (lead: SalesLeadRow) => void
  removeLead: (lead: SalesLeadRow) => void
  requestMove: (move: PendingMove) => void
  /** VRG-692 — a card dropped on the Lost column: confirm, with an optional reason. */
  requestLost: (deal: SalesDealCard) => void
  openProbabilities: () => void
  /** VRG-736 — Sales → Tag economics (tag unit cost and markup). */
  openTagEconomics: () => void
  /** VRG-723 — Create demo for this company: the Create demo modal, pre-picked on it. */
  createDemo: (accountId: string) => void
}

/**
 * VRG-691 — the Sales page's shared state: every form, the company drawer and the confirm
 * gates live HERE, once, and the Pipeline / Leads / Companies tabs and the header buttons call
 * into them. So "Add person" from the header, from the drawer and from the People tab is one form.
 */
export const useSalesWorkspace = () => {
  const { open: notify } = useSnackbar()
  const fail = (e: Error) => notify({ type: 'error', title: getErrorMessage(e) })

  // The board query is shared (react-query dedupes): it carries staff + probabilities.
  const { data: board } = useSalesBoard(null)
  const { data: accounts } = useSalesAccounts()
  const { data: leads } = useSalesLeads()
  const staff = board?.staff ?? []

  const [creatingLead, setCreatingLead] = useState<{ accountId: string | null } | null>(null)
  const [creatingCompany, setCreatingCompany] = useState(false)
  const [drawerId, setDrawerId] = useState<string | null>(null)
  const [drawerDealId, setDrawerDealId] = useState<string | null>(null)
  const [creatingDeal, setCreatingDeal] = useState<{ accountId: string | null } | null>(null)
  const [editingCompany, setEditingCompany] = useState(false)
  const [editingLead, setEditingLead] = useState<SalesLeadRow | null>(null)
  const [removingLead, setRemovingLead] = useState<SalesLeadRow | null>(null)
  const [editingDeal, setEditingDeal] = useState<SalesDealCard | null>(null)
  const [closingDeal, setClosingDeal] = useState<SalesDealCard | null>(null)
  const [losingDeal, setLosingDeal] = useState<SalesDealCard | null>(null)
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [probOpen, setProbOpen] = useState(false)
  const [tagOpen, setTagOpen] = useState(false)
  // VRG-723 — the company a demo is being created for (the modal opens pre-picked on it).
  const [demoForAccount, setDemoForAccount] = useState<string | null>(null)
  // Creating a demo is gated on the Demos permission, not on managing Sales (§46: disable + say why).
  const { isHasAccess: canCreateDemo } = useCanAccess({ action: 'demo_provision' })

  const { data: drawerAccount } = useSalesAccount(drawerId)
  const createLead = useCreateSalesLead()
  const updateLead = useUpdateSalesLead()
  const deleteLead = useDeleteSalesLead()
  const createAccount = useCreateSalesAccount()
  const createDeal = useCreateSalesDeal()
  const updateAccount = useUpdateSalesAccount()
  const updateDeal = useUpdateSalesDeal()
  const closeDeal = useCloseSalesDeal()
  const reopenDeal = useReopenSalesDeal()
  const move = useMoveSalesDeal()
  const settings = useUpdateSalesSettings()
  const tagSettings = useUpdateSalesTagEconomics()

  const doMove = (m: PendingMove, confirm: boolean) =>
    move.mutate(
      { id: m.deal.id, toStage: m.to, confirm, reopen: m.reopen },
      {
        onSuccess: (r) => {
          setPendingMove(null)
          notify({
            type: 'success',
            title: m.reopen
              ? `${m.deal.account_name} reopened at ${STAGE_LABEL[m.to]}${r.set_by === 'manual' ? ' (set by hand)' : ''}`
              : `${m.deal.account_name} moved to ${STAGE_LABEL[m.to]}${r.set_by === 'manual' ? ' (set by hand)' : ''}`,
          })
        },
        onError: (e) => {
          setPendingMove(null)
          fail(e as Error)
        },
      },
    )

  // Stable across renders (setters and `mutate` are stable), so the list columns memoise.
  const actions: SalesWorkspaceActions = useMemo(() => ({
    addLead: (accountId) => setCreatingLead({ accountId: accountId ?? null }),
    addCompany: () => setCreatingCompany(true),
    addDeal: (accountId) => setCreatingDeal({ accountId: accountId ?? null }),
    openCompany: (accountId, dealId) => {
      setDrawerId(accountId)
      setDrawerDealId(dealId ?? null)
    },
    editLead: setEditingLead,
    removeLead: setRemovingLead,
    // A move inside the evidence goes straight through (optimistic); past it, ask first.
    requestMove: (m) => (m.check.kind === 'confirm' ? setPendingMove(m) : doMove(m, false)),
    requestLost: setLosingDeal,
    openProbabilities: () => setProbOpen(true),
    openTagEconomics: () => setTagOpen(true),
    createDemo: (accountId) => setDemoForAccount(accountId),
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [])

  const accountOptions = (accounts ?? []).map((a) => ({ id: a.id, name: a.name }))

  const modals = (canManage: boolean) => (
    <>
      <SalesCompanyDrawer
        accountId={drawerId}
        dealId={drawerDealId}
        onSelectDeal={setDrawerDealId}
        onAddDeal={() => setCreatingDeal({ accountId: drawerId })}
        canManage={canManage}
        canCreateDemo={canCreateDemo}
        onCreateDemo={() => drawerId && setDemoForAccount(drawerId)}
        onClose={() => {
          setDrawerId(null)
          setDrawerDealId(null)
        }}
        onEditCompany={() => setEditingCompany(true)}
        onEditDeal={setEditingDeal}
        onAddLead={() => setCreatingLead({ accountId: drawerId })}
        onEditLead={setEditingLead}
        onRemoveLead={setRemovingLead}
        onCloseDeal={setClosingDeal}
        onReopenDeal={(d) =>
          reopenDeal.mutate(d.id, { onSuccess: () => notify({ type: 'success', title: 'Deal reopened' }), onError: (e) => fail(e as Error) })
        }
      />
      <CreateDemoModal open={!!demoForAccount} initialSalesAccountId={demoForAccount} onClose={() => setDemoForAccount(null)} />
      <LeadFormModal
        open={!!creatingLead || !!editingLead}
        lead={editingLead}
        accounts={accountOptions}
        staff={staff}
        defaultAccountId={creatingLead?.accountId}
        loading={createLead.isPending || updateLead.isPending}
        onClose={() => {
          setCreatingLead(null)
          setEditingLead(null)
        }}
        onSave={(input) => {
          const done = () => {
            setCreatingLead(null)
            setEditingLead(null)
          }
          if (editingLead) {
            updateLead.mutate({ id: editingLead.id, input }, { onSuccess: () => { done(); notify({ type: 'success', title: 'Person saved' }) }, onError: (e) => fail(e as Error) })
          } else {
            createLead.mutate(input, { onSuccess: () => { done(); notify({ type: 'success', title: 'Person added' }) }, onError: (e) => fail(e as Error) })
          }
        }}
      />
      <SalesCompanyFormModal
        open={creatingCompany || editingCompany}
        account={editingCompany ? drawerAccount : null}
        staff={staff}
        loading={createAccount.isPending || updateAccount.isPending}
        onClose={() => {
          setCreatingCompany(false)
          setEditingCompany(false)
        }}
        onSave={(input) => {
          if (editingCompany && drawerAccount) {
            updateAccount.mutate(
              { id: drawerAccount.id, input },
              { onSuccess: () => { setEditingCompany(false); notify({ type: 'success', title: 'Company saved' }) }, onError: (e) => fail(e as Error) },
            )
          } else {
            createAccount.mutate(input, {
              onSuccess: (r) => {
                setCreatingCompany(false)
                notify({ type: 'success', title: 'Company added as a Lead' })
                setDrawerId(r.id)
              },
              onError: (e) => fail(e as Error),
            })
          }
        }}
      />
      <DealFormModal
        open={!!editingDeal || !!creatingDeal}
        deal={editingDeal}
        accounts={accounts ?? []}
        leads={leads ?? []}
        staff={staff}
        defaultAccountId={creatingDeal?.accountId}
        loading={updateDeal.isPending || createDeal.isPending}
        onClose={() => {
          setEditingDeal(null)
          setCreatingDeal(null)
        }}
        onSave={(input) =>
          editingDeal &&
          updateDeal.mutate(
            { id: editingDeal.id, input },
            { onSuccess: () => { setEditingDeal(null); notify({ type: 'success', title: 'Deal saved' }) }, onError: (e) => fail(e as Error) },
          )
        }
        onCreate={(input) =>
          createDeal.mutate(input, {
            onSuccess: (r) => {
              setCreatingDeal(null)
              notify({
                type: r.warnings.includes('open_deal_exists') ? 'warning' : 'success',
                title: r.warnings.includes('open_deal_exists') ? 'Deal added. The company now has more than one open deal.' : 'Deal added',
              })
              setDrawerId(r.account_id)
              setDrawerDealId(r.id)
            },
            onError: (e) => fail(e as Error),
          })
        }
      />
      <CloseDealModal
        deal={closingDeal}
        loading={closeDeal.isPending}
        onClose={() => setClosingDeal(null)}
        onConfirm={(reason, note) =>
          closingDeal &&
          closeDeal.mutate(
            { id: closingDeal.id, reason, note },
            { onSuccess: () => { setClosingDeal(null); notify({ type: 'success', title: reason === 'lost' ? 'Deal marked lost' : 'Deal archived' }) }, onError: (e) => fail(e as Error) },
          )
        }
      />
      <CloseDealModal
        deal={losingDeal}
        lostOnly
        loading={closeDeal.isPending}
        onClose={() => setLosingDeal(null)}
        onConfirm={(_reason, note) =>
          losingDeal &&
          closeDeal.mutate(
            { id: losingDeal.id, reason: 'lost', note },
            { onSuccess: () => { setLosingDeal(null); notify({ type: 'success', title: `${dealTitle(losingDeal)} marked lost` }) }, onError: (e) => fail(e as Error) },
          )
        }
      />
      <ConfirmStageMoveModal move={pendingMove} loading={move.isPending} onClose={() => setPendingMove(null)} onConfirm={() => pendingMove && doMove(pendingMove, true)} />
      <StageProbabilitiesModal
        open={probOpen}
        value={board?.probabilities ?? DEFAULT_STAGE_PROBABILITIES}
        loading={settings.isPending}
        onClose={() => setProbOpen(false)}
        onSave={(p) =>
          settings.mutate(p, { onSuccess: () => { setProbOpen(false); notify({ type: 'success', title: 'Stage probabilities saved' }) }, onError: (e) => fail(e as Error) })
        }
      />
      {board?.tag_economics && (
        <TagEconomicsModal
          open={tagOpen}
          value={board.tag_economics}
          loading={tagSettings.isPending}
          onClose={() => setTagOpen(false)}
          onSave={(input) =>
            tagSettings.mutate(input, { onSuccess: () => { setTagOpen(false); notify({ type: 'success', title: 'Tag economics saved' }) }, onError: (e) => fail(e as Error) })
          }
        />
      )}
      <ConfirmSettingModal
        open={!!removingLead}
        onClose={() => setRemovingLead(null)}
        title="Remove person"
        consequence={`${removingLead?.email ?? 'This person'} leaves the sales pipeline. Their signup and Access row stay as they are.`}
        confirmLabel="Remove person"
        destructive
        loading={deleteLead.isPending}
        onConfirm={() =>
          removingLead &&
          deleteLead.mutate(removingLead.id, {
            onSuccess: () => { setRemovingLead(null); notify({ type: 'success', title: 'Person removed' }) },
            onError: (e) => fail(e as Error),
          })
        }
      />
    </>
  )

  return { actions, modals, staff, canCreateDemo }
}
